// InspectionRowActions — the inspections table's Actions cell: a kebab opening
// a Dropdown menu per the design: Edit, Duplicate, Suspend/Resume, Archive,
// Delete (same idiom as GuestRowActions). Every flow lives in InspectionPage
// via the callbacks; an ABSENT callback hides its item (hide-don't-disable), so
// this stays the seam for the future verb gating.
//
// Menu by state and tab:
//  - Duplicate/Suspend/Resume/Archive are AVAILABLE-tab only. On the Archived
//    tab they would be no-ops (there is no `restore` verb in the contract), so
//    they are not offered there at all.
//  - Delete is ARCHIVED-tab only, by the product rule: a live inspection can
//    only be archived, and only an archived one can be deleted (the same
//    archive-then-delete lifecycle the projects listing enforces).
//  - Drafts get neither the lifecycle verbs nor an archive, so on the Available
//    tab a draft offers Edit alone.

import { useState, type MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Dropdown, Icon, IconButton } from '@/components/common';
import { type InspectionState } from '@/api/endpoints/inspectionApi';

interface InspectionRowActionsProps {
  state: InspectionState;
  /** Archived tab — flips the menu from the lifecycle verbs to Delete. */
  archivedTab?: boolean;
  onEdit?: () => void;
  onDuplicate?: () => void;
  /** One callback for both directions: `suspended` rows offer Resume. */
  onSuspendResume?: () => void;
  onArchive?: () => void;
  onDelete?: () => void;
}

const InspectionRowActions = ({
  state,
  archivedTab = false,
  onEdit,
  onDuplicate,
  onSuspendResume,
  onArchive,
  onDelete,
}: InspectionRowActionsProps) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const draft = state === 'draft';
  const suspended = state === 'suspended';

  const stop = (e: MouseEvent) => e.stopPropagation();
  // Run a flow and close the menu behind it.
  const run = (fn: () => void) => () => {
    setOpen(false);
    fn();
  };

  // The lifecycle verbs: Available tab, non-draft rows, callback present.
  const lifecycle = !archivedTab && !draft;
  const showDuplicate = lifecycle && !!onDuplicate;
  const showSuspendResume = lifecycle && !!onSuspendResume;
  const showArchive = lifecycle && !!onArchive;
  const showDelete = archivedTab && !!onDelete;

  return (
    <span className='inspection-table__actions' onClick={stop}>
      <Dropdown
        open={open}
        onClose={() => setOpen(false)}
        align='end'
        width={180}
        portal
        trigger={
          <IconButton
            icon='ellipsis-vertical'
            variant='ghost'
            size='sm'
            aria-label={t('inspection_col_actions')}
            aria-expanded={open}
            onClick={() => setOpen((o) => !o)}
          />
        }
      >
        {onEdit && (
          <button
            type='button'
            className='app-dropdown__item'
            role='menuitem'
            onClick={run(onEdit)}
          >
            <Icon name='square-pen' size={15} />
            <span className='app-dropdown__item-label'>{t('inspection_action_edit')}</span>
          </button>
        )}
        {showDuplicate && (
          <button
            type='button'
            className='app-dropdown__item'
            role='menuitem'
            onClick={run(onDuplicate)}
          >
            <Icon name='copy' size={15} />
            <span className='app-dropdown__item-label'>{t('inspection_action_duplicate')}</span>
          </button>
        )}
        {showSuspendResume && (
          <button
            type='button'
            className='app-dropdown__item'
            role='menuitem'
            onClick={run(onSuspendResume)}
          >
            <Icon name={suspended ? 'play' : 'pause'} size={15} />
            <span className='app-dropdown__item-label'>
              {t(suspended ? 'inspection_action_resume' : 'inspection_action_suspend')}
            </span>
          </button>
        )}
        {showArchive && (
          <button
            type='button'
            className='app-dropdown__item'
            role='menuitem'
            onClick={run(onArchive)}
          >
            <Icon name='archive' size={15} />
            <span className='app-dropdown__item-label'>{t('inspection_action_archive')}</span>
          </button>
        )}
        {showDelete && (
          <>
            {onEdit && <div className='app-dropdown__divider' role='separator' />}
            <button
              type='button'
              className='app-dropdown__item app-dropdown__item--danger'
              role='menuitem'
              onClick={run(onDelete)}
            >
              <Icon name='trash-2' size={15} />
              <span className='app-dropdown__item-label'>{t('button_delete')}</span>
            </button>
          </>
        )}
      </Dropdown>
    </span>
  );
};

export default InspectionRowActions;
